import { useEffect, useState, useMemo } from 'react';
import { useSortBy, useTable } from 'react-table';
import {
  Button,
  Flex,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useDisclosure,
  Select,
} from '@chakra-ui/react';

import Card from '../../../../components/card/Card';
import ApplicationsApi, { Applications } from '../../../../api/applications';
import {
  tableColumns,
  TagActions,
  TagAmount,
  TagBranchName,
  TagDate,
  TagCompanyName,
  TagName,
  TagPhoneNumber,
  TagStatus,
  TagNotes,
  statusOptions,
} from './applicationsTableData';
import { getEndDate, prettifyTransferDate } from '../../../../functions/dateUtils';
import GenerateTransfersModal from './GenerateTransfersModal';
import EditApplicationModal from './EditApplicationModal';
import TransferDetailsModal from './TransferDetailsModal';
import EditUserModal from '../../users/components/EditUserModal';
import User from '../../../../api/user';
import CompanyDropdownSelection from '../../../../functions/CompanyDropdownSelection';
import MonthDropdownSelection from '../../../../functions/MonthDropdownSelection';
import { exportArrayToXlsxFile } from '../../../../functions/generatePayrollFile';

export default function AdminTransfersTable() {
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [rawData, setRawData] = useState([]);
  const data = useMemo(() => {
    if (selectedCompanies.length === 0) return rawData;
    const names = new Set(selectedCompanies.map((c) => c.name));
    return rawData.filter((row) => names.has(row.companyName));
  }, [rawData, selectedCompanies]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [applicationStatus, setApplicationStatus] = useState(''); // Default to pending transfers
  const [selectedApplication, setSelectedApplication] = useState(undefined);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userType, setUserType] = useState(-1);
  const [canExportPdfs, setCanExportPdfs] = useState(false);
  const [isExportingPdfs, setIsExportingPdfs] = useState(false);

  const columns = useMemo(() => {
    if (!data?.length) return tableColumns;

    const sampleRow = data[0];

    // Filter out columns that don't exist in the data, except for 'actions' column
    return tableColumns.filter(
      (column) =>
        column.accessor === 'actions' ||
        column.tag === TagBranchName ||
        column.accessor in sampleRow ||
        data.some((row) => column.accessor in row),
    );
  }, [data]);

  const refreshApplications = () => {
    ApplicationsApi.getAll(applicationStatus, startDate, endDate)
      .then((res) => {
        if (res && res.data) {
          try {
            res.data.sort(function (a, b) {
              return new Date(b['createdAt']) - new Date(a['createdAt']);
            });
          } catch (e) {}
        }
        setRawData(res.data);
      })
      .catch(() => {
        setRawData([]);
      });
  };

  useEffect(() => {
    const userInfo = User.GetInfo();
    setUserType(userInfo.type);
    setCanExportPdfs(userInfo.type === 1 || (userInfo.type === 2 && userInfo.companyId === 69));
    refreshApplications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable(
    { columns, data },
    useSortBy,
  );
  const filteredDataByCompany = (companies) => {
    setSelectedCompanies(companies);
  };

  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');

  const {
    isOpen: isExportModalOpen,
    onOpen: onExportModalOpen,
    onClose: onExportModalClose,
  } = useDisclosure();

  const { isOpen: isEditModalOpen, onClose: onEditModalClose } = useDisclosure();

  const {
    isOpen: isEditUserModalOpen,
    onOpen: onEditUserModalOpen,
    onClose: onEditUserModalClose,
  } = useDisclosure();

  const {
    isOpen: isTransferDetailsModalOpen,
    onOpen: onTransferDetailsModalOpen,
    onClose: onTransferDetailsModalClose,
  } = useDisclosure();

  const handleRefreshClick = () => {
    refreshApplications();
  };

  const handleExportApplicationsClick = () => {
    onExportModalOpen();
  };

  const handleExportAllPdfsClick = async () => {
    const eligibleApps = data.filter(
      (app) =>
        (app.status === 500 || app.status === 700) &&
        app.companyId === 69,
    );
    if (eligibleApps.length === 0) {
      alert('אין אישורים להורדה עבור הסינון הנוכחי');
      return;
    }
    setIsExportingPdfs(true);
    try {
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();
      for (const app of eligibleApps) {
        const buffer = await ApplicationsApi.fetchApprovalPdfBuffer(app.id);
        const filename = `approval-${app.id}-${(app.fullName || '').replace(/\s+/g, '_')}.pdf`;
        zip.file(filename, buffer);
      }
      const content = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(content);
      const a = document.createElement('a');
      a.href = url;
      a.download = `approvals-${new Date().toISOString().slice(0, 10)}.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Failed to export PDFs', e);
      alert('שגיאה בייצוא הקבצים');
    } finally {
      setIsExportingPdfs(false);
    }
  };

  const handleExportToExcelClick = () => {
    const filteredData = data.map((row) => {
      return {
        companyName: row.companyName,
        fullName: row.fullName,
        phoneNumber: row.phoneNumber || '',
        branch: row.branch || '',
        amount: row.amount,
        status: Applications.statusText(row.status),
        createdAt: row.createdAt,
      };
    });

    exportArrayToXlsxFile(filteredData);
  };

  const handleStatusChange = (event) => {
    const value = event.target.value;
    setApplicationStatus(value);
  };

  const handleEditClick = (cellRow) => {
    setSelectedApplication(data[cellRow.index]);
    onTransferDetailsModalOpen();
  };

  const handleUserNameClick = (cellRow) => {
    const application = data[cellRow.index];
    // Create a user object with the necessary properties for EditUserModal
    const user = {
      id: application.userId,
      companyId: application.companyId,
      fullName: application.fullName,
    };
    setSelectedUser(user);
    onEditUserModalOpen();
  };

  const modalsCallBack = () => {
    refreshApplications();
  };

  const setStartAndEndDate = (selectedMonth) => {
    const [month, year] = selectedMonth.split('/');
    const startDate = `01/${month}/${year}`;
    const endDate = getEndDate(month, year);
    setStartDate(startDate);
    setEndDate(endDate);
  };

  return (
    <Card direction="column" w="100%" px="0px" overflowX={{ sm: 'scroll', lg: 'hidden' }}>
      <Flex px="25px" mb="20px" align="center">
        <MonthDropdownSelection handleSelectedMonth={setStartAndEndDate} />

        <GenerateTransfersModal
          applications={data}
          isOpen={isExportModalOpen}
          onClose={onExportModalClose}
        />

        <EditApplicationModal
          application={selectedApplication}
          isOpen={isEditModalOpen}
          onClose={onEditModalClose}
        />

        <TransferDetailsModal
          application={selectedApplication}
          isOpen={isTransferDetailsModalOpen}
          onClose={onTransferDetailsModalClose}
        />

        <EditUserModal
          employee={selectedUser}
          callBack={modalsCallBack}
          isOpen={isEditUserModalOpen}
          onClose={onEditUserModalClose}
        />

        <Select
          marginRight="30px"
          width="190px"
          pr="15px"
          onChange={handleStatusChange}
          value={applicationStatus}
        >
          <option value="88" disabled hidden>
            בחר סטטוס העברה
          </option>
          {statusOptions.map((option) => (
            <option key={option.val} value={option.val}>
              {option.label}
            </option>
          ))}
        </Select>

        <CompanyDropdownSelection selectedCompany={filteredDataByCompany} showAll={true} />

        <Button marginRight="30px" width="80px" variant="blue" onClick={handleRefreshClick}>
          הצג
        </Button>
      </Flex>
      <Flex px="0px" mb="20px" align="center">
        <Button
          marginRight="30px"
          width="150px"
          variant="blue"
          visibility={userType === 1 ? 'visible' : 'hidden'}
          onClick={handleExportApplicationsClick}
        >
          קובץ העברות לבנק
        </Button>

        <Button
          marginRight="30px"
          width="150px"
          variant="blue"
          visibility={userType === 1 ? 'visible' : 'hidden'}
          onClick={handleExportToExcelClick}
        >
          ייצוא לאקסל
        </Button>

        <Button
          marginRight="30px"
          width="170px"
          variant="blue"
          visibility={canExportPdfs ? 'visible' : 'hidden'}
          onClick={handleExportAllPdfsClick}
          isLoading={isExportingPdfs}
          loadingText="מייצא..."
        >
          ייצוא אישורי העברות
        </Button>
      </Flex>

      <Table {...getTableProps()} variant="simple" color="gray.500" mb="24px">
        <Thead>
          {headerGroups.map((headerGroup, index) => (
            <Tr {...headerGroup.getHeaderGroupProps()} key={index}>
              {headerGroup.headers.map((column, index) => (
                <Th
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                  pe="10px"
                  key={index}
                  borderColor={borderColor}
                >
                  <Flex
                    justify="space-between"
                    align="center"
                    fontSize={{ sm: '10px', lg: '12px' }}
                    color="gray.400"
                  >
                    {column.render('Header')}
                  </Flex>
                </Th>
              ))}
            </Tr>
          ))}
        </Thead>

        <Tbody {...getTableBodyProps()}>
          {rows.map((row, index) => {
            prepareRow(row);
            return (
              <Tr {...row.getRowProps()} key={index}>
                {row.cells.map((cell, index) => {
                  let data = '';
                  let cellWidth = cell.column.width;
                  if (cell.column.tag === TagAmount) {
                    data = (
                      <Text color={textColor} fontSize="sm" fontWeight="700">
                        ₪{Number(cell.value).toLocaleString('en-US')}
                      </Text>
                    );
                  } else if (cell.column.tag === TagCompanyName) {
                    data = (
                      <Text color={textColor} fontSize="sm" fontWeight="700">
                        {cell.value}
                      </Text>
                    );
                  } else if (cell.column.tag === TagBranchName) {
                    data = (
                      <Text color={textColor} fontSize="sm" fontWeight="700">
                        {cell.value}
                      </Text>
                    );
                  } else if (cell.column.tag === TagName) {
                    // Check if this is the fullName column (employee name)
                    const application = row.original;
                    const isEditable = application?.isEditable !== false;

                    if (isEditable) {
                      // Render as clickable for users with edit permissions
                      data = (
                        <Text
                          color="blue.500"
                          fontSize="sm"
                          fontWeight="700"
                          cursor="pointer"
                          textDecoration="underline"
                          onClick={() => handleUserNameClick(cell.row)}
                        >
                          {cell.value}
                        </Text>
                      );
                    } else {
                      // Render as plain text for users without edit permissions
                      data = (
                        <Text color={textColor} fontSize="sm" fontWeight="700">
                          {cell.value}
                        </Text>
                      );
                    }
                  } else if (cell.column.tag === TagPhoneNumber) {
                    data = (
                      <Text color={textColor} fontSize="sm" fontWeight="700">
                        {cell.value || '-'}
                      </Text>
                    );
                  } else if (cell.column.tag === TagStatus) {
                    data = (
                      <Text color={textColor} fontSize="sm" fontWeight="700">
                        {Applications.statusText(cell.value)}
                      </Text>
                    );
                  } else if (cell.column.tag === TagDate) {
                    data = (
                      <Text color={textColor} fontSize="sm" fontWeight="700">
                        {prettifyTransferDate(cell.value)}
                      </Text>
                    );
                  } else if (cell.column.tag === TagNotes) {
                    const truncatedNotes = cell.value
                      ? cell.value.length > 50
                        ? cell.value.substring(0, 50) + '...'
                        : cell.value
                      : '';
                    data = (
                      <Text color={textColor} fontSize="sm" fontWeight="700">
                        {truncatedNotes}
                      </Text>
                    );
                  } else if (cell.column.tag === TagActions) {
                    data = (
                      <Flex align="center">
                        <Button
                          onClick={() => handleEditClick(cell.row)}
                          marginLeft="20px"
                          textColor="black"
                          backgroundColor="green.200"
                          height="35px"
                        >
                          פרטים
                        </Button>
                      </Flex>
                    );
                  }
                  return (
                    <Td
                      {...cell.getCellProps()}
                      key={index}
                      fontSize={{ sm: '14px' }}
                      width={cellWidth}
                      borderColor="transparent"
                    >
                      {data}
                    </Td>
                  );
                })}
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </Card>
  );
}
